/**
 * DATA LAYER (DL): ESQUIVADOR (SURFER - DINÁMICA ID 51)
 * Módulo de comunicación nativa HTTP / REST con el backend de Interaktia-V2 para la dinámica Esquivador.
 * Gestiona el CRUD de temas visuales multi-tenant, optimización de medios con SkiaSharp,
 * configuración técnica de sala, historial de partidas y control autoritativo de partidas 1 vs 1 en tiempo real.
 * 
 * @module utilities/DL/Surfer
 */

/**
 * Resuelve la URL base del API eliminando barras inclinadas al final.
 * @returns {string} URL base normalizada del API.
 */
const getBaseApiUrl = () => {
    const raw = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
};

/**
 * Resuelve el endpoint base para el controlador de Esquivador.
 * @returns {string} Endpoint base de Surfer (/v1/Surfer).
 */
const getSurferApiBaseUrl = () => {
    return `${getBaseApiUrl()}/v1/Surfer`;
};

// =========================================================================
// GESTIÓN DE TEMAS VISUALES (CRUD & CLONACIÓN)
// =========================================================================

/**
 * Obtiene la lista de temas visuales disponibles con filtrado multi-tenant opcional por sala u operador.
 * @param {string|null} [roomId=null] - GUID de la sala.
 * @param {string|null} [operatorId=null] - GUID del operador.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetThemesDL = async (roomId = null, operatorId = null) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId) queryParams.append('roomId', String(roomId).trim());
        if (operatorId) queryParams.append('operatorId', String(operatorId).trim());

        const queryString = queryParams.toString();
        const url = `${getSurferApiBaseUrl()}/themes${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al consultar los temas de Esquivador.`,
                data: []
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al consultar la lista de temas de Esquivador.',
            data: []
        };
    }
};

/**
 * Obtiene el detalle completo de un tema específico por su ID.
 * @param {string|number} id - GUID o identificador del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetThemeByIdDL = async (id) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/theme/${id}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al obtener el tema.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al obtener el detalle del tema de Esquivador.'
        };
    }
};

/**
 * Registra un nuevo tema visual personalizable para Esquivador.
 * @param {Object} themeData - Datos del tema a crear.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CreateThemeDL = async (themeData) => {
    try {
        if (!themeData || !themeData.name) {
            return { success: false, message: 'El nombre del tema es obligatorio.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/theme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(themeData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al crear el tema.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Tema creado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al registrar el nuevo tema de Esquivador.'
        };
    }
};

/**
 * Actualiza un tema visual existente de Esquivador.
 * @param {string|number} id - GUID o identificador del tema.
 * @param {Object} themeData - Datos actualizados del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const UpdateThemeDL = async (id, themeData) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio para actualización.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/theme/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(themeData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al actualizar el tema.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Tema actualizado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al actualizar el tema de Esquivador.'
        };
    }
};

/**
 * Elimina un tema visual personalizado de Esquivador.
 * @param {string|number} id - GUID o identificador del tema.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DeleteThemeDL = async (id) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio para eliminar.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/theme/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al eliminar el tema.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            message: data.message || 'Tema eliminado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al eliminar el tema de Esquivador.'
        };
    }
};

/**
 * Clona un tema existente (incluyendo presets del sistema) para personalización.
 * @param {string|number} id - GUID o identificador del tema origen.
 * @param {string|null} [newName=null] - Nombre para la nueva copia.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CloneThemeDL = async (id, newName = null) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema origen es obligatorio.' };
        }

        const queryParams = new URLSearchParams();
        if (newName) queryParams.append('newName', String(newName).trim());

        const queryString = queryParams.toString();
        const url = `${getSurferApiBaseUrl()}/theme/${id}/clone${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al clonar el tema.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Tema clonado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al clonar el tema de Esquivador.'
        };
    }
};

// =========================================================================
// CONFIGURACIÓN TÉCNICA DE SALA
// =========================================================================

/**
 * Obtiene la configuración técnica y parámetros de juego activos de Esquivador para una sala.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetConfigDL = async (roomId) => {
    try {
        if (!roomId) {
            return { success: false, message: 'El RoomId es obligatorio.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/config/${roomId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al obtener configuración de sala.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al consultar la configuración de Esquivador.'
        };
    }
};

/**
 * Guarda o actualiza los parámetros y reglas de juego de Esquivador para una sala.
 * @param {Object} configData - Objeto con parámetros de configuración (RoomId obligatorio).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SaveConfigDL = async (configData) => {
    try {
        if (!configData || !configData.roomId) {
            return { success: false, message: 'El RoomId es obligatorio para guardar la configuración.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(configData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al guardar configuración.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Configuración guardada exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al guardar la configuración de Esquivador.'
        };
    }
};

// =========================================================================
// HISTORIAL DE PARTIDAS Y RESULTADOS
// =========================================================================

/**
 * Consulta el historial de partidas disputadas en una sala.
 * @param {string} roomId - GUID de la sala.
 * @param {number} [limit=20] - Cantidad máxima de registros a retornar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetMatchHistoryDL = async (roomId, limit = 20) => {
    try {
        if (!roomId) {
            return { success: false, message: 'El RoomId es obligatorio.', data: [] };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/history/${roomId}?limit=${limit}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al consultar el historial.`,
                data: []
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al obtener el historial de partidas de Esquivador.',
            data: []
        };
    }
};

/**
 * Guarda y persiste el resultado y estadísticas finales de una partida.
 * @param {Object} matchData - Datos de la partida finalizada.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SaveMatchResultDL = async (matchData) => {
    try {
        if (!matchData || !matchData.roomId) {
            return { success: false, message: 'El RoomId es obligatorio para guardar el resultado.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/history`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(matchData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al guardar resultado.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Resultado de la partida guardado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al guardar el resultado de la partida de Esquivador.'
        };
    }
};

// =========================================================================
// SUBIDA Y OPTIMIZACIÓN MULTIMEDIA (SKIASHARP)
// =========================================================================

/**
 * Procesa y sube un archivo multimedia individual optimizándolo con SkiaSharp a WebP.
 * @param {File} file - Archivo a subir.
 * @param {string} [assetType=''] - Tipo de recurso (background, cockpit, road, sideprop, obstacle, coin, bonus, player1, player2).
 * @returns {Promise<{success: boolean, url?: string, message?: string, data?: Object}>}
 */
export const UploadAssetDL = async (file, assetType = '') => {
    try {
        if (!file) {
            return { success: false, message: 'No se seleccionó ningún archivo para subir.' };
        }

        const formData = new FormData();
        formData.append('file', file);

        const url = `${getSurferApiBaseUrl()}/upload${assetType ? `?assetType=${encodeURIComponent(assetType)}` : ''}`;

        const response = await fetch(url, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al subir archivo multimedia.`
            };
        }

        const data = await response.json();
        return {
            success: data.success !== undefined ? data.success : true,
            url: data.url || (data.data && data.data.url) || '',
            message: data.message || 'Archivo subido y optimizado con éxito.',
            data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red durante la subida del archivo multimedia.'
        };
    }
};

/**
 * Procesa y sube un lote de múltiples archivos multimedia.
 * @param {Array<File>|FileList} files - Colección de archivos a procesar.
 * @param {string} [assetType=''] - Tipo de recurso asignado al lote.
 * @returns {Promise<{success: boolean, urls?: Array<string>, message?: string, data?: Object}>}
 */
export const UploadBatchMediaDL = async (files, assetType = '') => {
    try {
        if (!files || files.length === 0) {
            return { success: false, message: 'No se proporcionaron archivos para el lote.' };
        }

        const formData = new FormData();
        Array.from(files).forEach((file) => {
            formData.append('files', file);
        });

        const url = `${getSurferApiBaseUrl()}/upload-batch${assetType ? `?assetType=${encodeURIComponent(assetType)}` : ''}`;

        const response = await fetch(url, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al subir lote multimedia.`
            };
        }

        const data = await response.json();
        return {
            success: data.success !== undefined ? data.success : true,
            urls: data.urls || [],
            message: data.message || 'Lote procesado exitosamente.',
            data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red durante la subida del lote multimedia.'
        };
    }
};

// =========================================================================
// CONTROL AUTORITATIVO DE TIEMPO REAL (OPERADOR DEL ESTADIO)
// =========================================================================

/**
 * Proyecta la portada de Esquivador en la pantalla gigante y móviles.
 * @param {string} roomId - GUID de la sala.
 * @param {string|null} [themeId=null] - GUID opcional del tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ShowCoverDL = async (roomId, themeId = null) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/cover`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId, themeId: themeId || null })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al proyectar la portada.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Portada proyectada exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al proyectar la portada de Esquivador.'
        };
    }
};

/**
 * Proyecta el escenario de juego 1 vs 1 de Esquivador preparando los corredores y cargando el tema.
 * @param {string} roomId - GUID de la sala.
 * @param {string|null} [themeId=null] - GUID opcional del tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ShowGameSceneDL = async (roomId, themeId = null) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/game-scene`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId, themeId: themeId || null })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al proyectar el juego.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Escenario de juego proyectado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al proyectar el juego de Esquivador.'
        };
    }
};

/**
 * Inicia la secuencia oficial de partida (conteo regresivo 3..2..1 -> fase running).
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const StartMatchDL = async (roomId) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al iniciar la partida.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Secuencia de partida iniciada.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al iniciar la carrera de Esquivador.'
        };
    }
};

/**
 * Pausa una partida en curso.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const PauseMatchDL = async (roomId) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/pause`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al pausar la partida.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Partida pausada exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al pausar la partida de Esquivador.'
        };
    }
};

/**
 * Reanuda una partida de Esquivador previamente pausada.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ResumeMatchDL = async (roomId) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/resume`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al reanudar la partida.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Partida reanudada exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al reanudar la partida de Esquivador.'
        };
    }
};

/**
 * Reinicia la partida devolviéndola al estado 'game_ready' preservando conexiones y tema.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ResetMatchDL = async (roomId) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al reiniciar la partida.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Partida reiniciada exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al reiniciar la carrera de Esquivador.'
        };
    }
};

/**
 * Finaliza anticipadamente la partida evaluando el estado actual y persistiendo los resultados.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const FinishEarlyDL = async (roomId) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/control/finish-early`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al finalizar anticipadamente.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Partida finalizada anticipadamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al forzar el fin temprano de Esquivador.'
        };
    }
};

/**
 * Consulta el estado en vivo autoritativo en memoria de la partida de Esquivador.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetMatchStateDL = async (roomId) => {
    try {
        if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };

        const response = await fetch(`${getSurferApiBaseUrl()}/state/${roomId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al consultar el estado.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al consultar el estado en vivo de Esquivador.'
        };
    }
};

// =========================================================================
// INTERACCIÓN MÓVIL DE AFICIONADOS (FAN CONTROLLER)
// =========================================================================

/**
 * Asigna a un aficionado al slot de corredor seleccionado ("player1" o "player2").
 * @param {string} roomId - GUID de la sala.
 * @param {string} connectionId - ID de conexión SignalR.
 * @param {string} side - Bando ("player1" o "player2").
 * @param {string} playerName - Nombre o alias del jugador.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SelectPlayerSideDL = async (roomId, connectionId, side, playerName) => {
    try {
        if (!roomId || !side) {
            return { success: false, message: 'El RoomId y el bando (side) son obligatorios.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/fan/select-side`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                roomId,
                connectionId: connectionId || '',
                side,
                playerName: playerName || ''
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al seleccionar lado.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || 'Lado asignado exitosamente.'
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al asignar lado de Esquivador.'
        };
    }
};

/**
 * Envía una acción de movimiento o maniobra emitida desde el móvil del aficionado.
 * @param {string} roomId - GUID de la sala.
 * @param {string} connectionId - ID de conexión SignalR.
 * @param {string} side - Bando ("player1" o "player2").
 * @param {string} action - Acción ("move_left", "move_right", "jump", "slide").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SendPlayerActionDL = async (roomId, connectionId, side, action) => {
    try {
        if (!roomId || !side || !action) {
            return { success: false, message: 'El RoomId, side y action son obligatorios.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/fan/action`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                roomId,
                connectionId: connectionId || '',
                side,
                action
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al enviar acción.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al enviar acción del jugador.'
        };
    }
};

/**
 * Reporta una colisión de un corredor contra un obstáculo.
 * @param {string} roomId - GUID de la sala.
 * @param {string} side - Bando que colisionó ("player1" o "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ReportHitDL = async (roomId, side) => {
    try {
        if (!roomId || !side) {
            return { success: false, message: 'El RoomId y side son obligatorios.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/fan/report-hit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId, side })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al reportar choque.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al reportar colisión.'
        };
    }
};

/**
 * Actualiza la puntuación de un participante en vivo.
 * @param {string} roomId - GUID de la sala.
 * @param {string} side - Bando ("player1" o "player2").
 * @param {number} pointsDelta - Puntos a sumar o restar.
 * @param {string|null} [reason=null] - Motivo del cambio.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const UpdateScoreDL = async (roomId, side, pointsDelta, reason = null) => {
    try {
        if (!roomId || !side) {
            return { success: false, message: 'El RoomId y side son obligatorios.' };
        }

        const response = await fetch(`${getSurferApiBaseUrl()}/control/update-score`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId, side, pointsDelta, reason })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Error HTTP ${response.status} al actualizar puntuación.`
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data.data !== undefined ? data.data : data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al actualizar puntaje en Esquivador.'
        };
    }
};

const SurferDL = {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartMatchDL,
    PauseMatchDL,
    ResumeMatchDL,
    ResetMatchDL,
    FinishEarlyDL,
    GetMatchStateDL,
    SelectPlayerSideDL,
    SendPlayerActionDL,
    ReportHitDL,
    UpdateScoreDL
};

export default SurferDL;
